import Button, { ButtonProps } from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import CreateIcon from "@material-ui/icons/Create";
import clsx from "clsx";
import * as React from "react";

import {
  buttonBaseStyle,
  buttonSizeStyles,
  buttonToneStyles,
  ButtonSize,
  ButtonTone,
  legacyButtonTones,
  LegacyButtonVariant,
  resolveButtonSize,
} from "@/buttonStyles";
import { buildAutomationAttributes } from "@/utilities/automation";

import ThinArrowIcon from "./icon/ThinArrowIcon";

const useStyles = makeStyles(() => ({
  "@keyframes arrow1Cycle": {
    "0%": { opacity: 0, transform: "translateX(0)" },
    "15%": { opacity: 0, transform: "translateX(-5px)" },
    "35%": { opacity: 1, transform: "translateX(0)" },
    "55%": { opacity: 1, transform: "translateX(0)" },
    "75%": { opacity: 0, transform: "translateX(0)" },
    "100%": { opacity: 0, transform: "translateX(0)" },
  },
  "@keyframes arrow2Cycle": {
    "0%": { opacity: 0, transform: "translateX(-5px)" },
    "20%": { opacity: 1, transform: "translateX(0)" },
    "50%": { opacity: 1, transform: "translateX(0)" },
    "65%": { opacity: 0, transform: "translateX(0)" },
    "100%": { opacity: 0, transform: "translateX(0)" },
  },
  root: { display: "flex", width: "100%" },
  button: { "&&&": { ...buttonBaseStyle, display: "flex", margin: "0 auto" } },
  L: { "&&&": buttonSizeStyles.L },
  M: { "&&&": buttonSizeStyles.M },
  S: { "&&&": buttonSizeStyles.S },
  primary: { "&&&": buttonToneStyles.primary },
  trust: { "&&&": buttonToneStyles.trust },
  outline: { "&&&": buttonToneStyles.outline },
  text: { "&&&": buttonToneStyles.text },
  inverse: { "&&&": buttonToneStyles.inverse },
  defaultWidth: { maxWidth: 350 },
  filledForApprove: { flex: 3, minWidth: 100, maxWidth: 350 },
  filledForAgentSearch: { minWidth: 90, maxWidth: 150, "@media (max-width:600px)": { width: 121 } },
  filledForProfileDetail: { maxWidth: 150 },
  filledGrayForLater: { minWidth: 75, flex: 1 },
  filledOrangeForMsins: { maxWidth: 380, width: 380, "@media (max-width:767px)": { maxWidth: 325, width: 325 } },
  outlinedGrayForPendingOffer: { maxWidth: 75 },
  arrowPair: { display: "inline-flex", alignItems: "center" },
  arrow: { willChange: "transform, opacity", fontSize: 28, marginLeft: 0 },
  arrow1: { animation: "$arrow1Cycle 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite both", marginLeft: -8 },
  arrow2: { animation: "$arrow2Cycle 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite both", marginLeft: -18 },
}));

export interface CustomButtonProps extends ButtonProps {
  customVariant?: LegacyButtonVariant;
  tone?: ButtonTone;
  buttonSize?: ButtonSize;
  isEdit?: boolean;
  component?: React.ElementType;
  withArrow?: boolean;
  qaId?: string;
  testId?: string;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  customVariant,
  tone,
  buttonSize,
  children,
  isEdit,
  withArrow,
  className,
  fullWidth,
  size,
  qaId,
  testId,
  ...props
}) => {
  const classes = useStyles();
  const resolvedTone = tone ?? (customVariant ? legacyButtonTones[customVariant] : "primary");
  const resolvedSize = buttonSize ?? resolveButtonSize(customVariant, size);
  const layoutClass =
    customVariant && customVariant in classes ? classes[customVariant as keyof typeof classes] : undefined;
  const automationAttributes = buildAutomationAttributes({ qaId, testId, componentName: "custom-button" });
  return (
    <div className={classes.root}>
      <Button
        fullWidth={fullWidth ?? true}
        className={clsx(
          classes.button,
          classes[resolvedTone],
          classes[resolvedSize],
          !fullWidth && classes.defaultWidth,
          layoutClass,
          className
        )}
        {...automationAttributes}
        size={size}
        {...props}
        data-button-tone={resolvedTone}
        data-button-size={resolvedSize}
        startIcon={isEdit && <CreateIcon />}
        endIcon={
          withArrow ? (
            <span className={classes.arrowPair}>
              <ThinArrowIcon className={clsx(classes.arrow, classes.arrow1)} />
              <ThinArrowIcon className={clsx(classes.arrow, classes.arrow2)} />
            </span>
          ) : undefined
        }
      >
        {children}
      </Button>
    </div>
  );
};

const ButtonGroup: React.FC = () => {
  return (
    <Grid container spacing={2} direction="column">
      <Grid item container spacing={2}>
        <Grid item>
          <CustomButton customVariant="filled">新規会員登録</CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedPrimary">新規会員登録</CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedGray">新規会員登録</CustomButton>
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item>
          <CustomButton customVariant="filled">ラベル</CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedPrimary">ラベル</CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedGray">ラベル</CustomButton>
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item>
          <CustomButton customVariant="filled" disabled>
            Filled/Disabled
          </CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedPrimary" disabled>
            OutlinedPrimary/Disabled
          </CustomButton>
        </Grid>
        <Grid item>
          <CustomButton customVariant="outlinedGray" disabled>
            OutlinedGray/Disabled
          </CustomButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ButtonGroup;
